import { extractErrorMessage, isReloginRequired } from './errors';
import { httpStatus } from './http-status';

export interface ExitImpersonationDeps {
  /**
   * `POST /auth/exit-impersonation` through the auth store, which on success
   * reloads the page onto /people itself — nothing is left to do here. [HW-872]
   */
  exit: () => Promise<void>;
  /** The auth store's logout — clears every auth cookie, `admin_token` included. */
  logout: () => Promise<void>;
  /** Shows a message to the user (a toast). */
  notify: (message: string) => void;
  /** Full reload onto the login page. */
  toLogin: () => void;
  /** Full reload onto the home page, under whatever session the cookies hold. */
  toHome: () => void;
}

/** The server's message as a sentence, so a notice can follow it. */
function asSentence(message: string): string {
  const text = message.trim();
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

/**
 * The impersonation banner's Exit. A refused exit used to be swallowed,
 * leaving a button that did nothing; now each refusal is answered by what it
 * means [HW-942]:
 *
 * - `reloginRequired` — the server could not restore the admin and has cleared
 *   every cookie: the admin is told why, signed out and sent to the login page.
 * - 400, "Not currently impersonating" — this browser's session is already the
 *   admin's own (e.g. they signed in again in another tab), only this tab still
 *   shows the banner: it reloads into that session. Signing out here would end
 *   the session the other tab just started.
 * - any other 401 — the refresh interceptor has already acted on it: it either
 *   found the impersonation over and is reloading the page, which signs the
 *   admin back in, or found the session expired and sent the app to the login
 *   page. Signing out would turn the first into a sign-out. The page is
 *   reloaded onto the home page all the same — harmless after either, and the
 *   way out if neither happened (a retried exit refused again), so the button
 *   never does nothing.
 * - anything else (no answer, a server error) — the admin is told, and the
 *   button can be tried again.
 *
 * Resolves `'stayed'` only in that last case: everywhere else the page is on
 * its way out, and the banner keeps its button disabled until it is gone.
 */
export async function exitImpersonationOrSignOut(
  deps: ExitImpersonationDeps
): Promise<'leaving' | 'stayed'> {
  try {
    await deps.exit();
  } catch (err) {
    if (isReloginRequired(err)) {
      // The server's reason may already say "Please log in again"; one
      // sign-out notice after it is enough.
      deps.notify(
        `${asSentence(extractErrorMessage(err, 'Could not exit the impersonation.'))} Signing you out.`
      );
      try {
        await deps.logout();
      } catch {
        // The reload below still leaves the impersonated session.
      }
      deps.toLogin();
      return 'leaving';
    }
    const status = httpStatus(err);
    if (status === 400) {
      deps.notify(
        `${asSentence(extractErrorMessage(err, 'Not currently impersonating.'))} Taking you back to your own session.`
      );
      deps.toHome();
      return 'leaving';
    }
    if (status === 401) {
      deps.toHome();
      return 'leaving';
    }
    deps.notify(
      `${asSentence(extractErrorMessage(err, 'Could not exit the impersonation.'))} Please try again.`
    );
    return 'stayed';
  }
  return 'leaving';
}
